import React from 'react';
import { formatDatePSTShort } from '../../utils/dateUtils';
import './BillingDashboard.css';

interface PaymentPlanCardProps {
  plan: {
    id: string;
    plan_type: string;
    plan_status: string;
    billing_cycle: string;
    start_date: string;
    end_date: string;
    total_amount: number;
    monthly_amount?: number;
    installment_count?: number;
    installment_number?: number;
    next_billing_date?: string;
    student_id: string;
    student_name?: string;
  };
  onView: (planId: string) => void;
  onCancel?: (planId: string) => void;
  onCreateInvoice?: (planId: string) => void;
}

const PaymentPlanCard: React.FC<PaymentPlanCardProps> = ({ plan, onView, onCancel, onCreateInvoice }) => {
  const getStatusBadge = (status: string) => {
    const statusClasses: { [key: string]: string } = {
      'active': 'status-badge status-active',
      'completed': 'status-badge status-completed',
      'cancelled': 'status-badge status-cancelled',
      'paused': 'status-badge status-paused'
    };
    
    return (
      <span className={statusClasses[status] || 'status-badge'}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getPlanTypeLabel = (planType: string) => {
    const labels: { [key: string]: string } = {
      'monthly': 'Monthly Subscription',
      'session_package': 'Session Package',
      'camp': 'Camp Enrollment',
      'competition': 'Competition (FLL)'
    };
    return labels[planType] || planType;
  };

  const progress = plan.installment_count && plan.installment_number
    ? Math.round((plan.installment_number / plan.installment_count) * 100)
    : 0;

  return (
    <div className="payment-plan-card">
      <div className="payment-plan-card-header">
        <div className="payment-plan-card-title">
          <h3>{getPlanTypeLabel(plan.plan_type)}</h3>
          {getStatusBadge(plan.plan_status)}
        </div>
        <div className="payment-plan-card-amount">
          <span className="amount-label">Total</span>
          <span className="amount-value">${plan.total_amount.toFixed(2)}</span>
        </div>
      </div>
      
      <div className="payment-plan-card-body">
        <div className="payment-plan-card-info">
          {plan.student_name && (
            <div className="info-row">
              <span className="info-label">Student:</span>
              <span className="info-value">{plan.student_name}</span>
            </div>
          )}
          <div className="info-row">
            <span className="info-label">Billing Cycle:</span>
            <span className="info-value">{plan.billing_cycle}</span>
          </div>
          {plan.monthly_amount && (
            <div className="info-row">
              <span className="info-label">Monthly Amount:</span>
              <span className="info-value">${plan.monthly_amount.toFixed(2)}</span>
            </div>
          )}
          {plan.next_billing_date && (
            <div className="info-row">
              <span className="info-label">Next Payment:</span>
              <span className="info-value">{formatDatePSTShort(plan.next_billing_date)}</span>
            </div>
          )}
          {plan.installment_count && plan.installment_number !== undefined && (
            <div className="info-row">
              <span className="info-label">Progress:</span>
              <div className="progress-container">
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
                <span className="progress-text">
                  {plan.installment_number} of {plan.installment_count} installments
                </span>
              </div>
            </div>
          )}
          <div className="info-row">
            <span className="info-label">Period:</span>
            <span className="info-value">
              {formatDatePSTShort(plan.start_date)} - {formatDatePSTShort(plan.end_date)}
            </span>
          </div>
        </div>
      </div>
      
      <div className="payment-plan-card-actions">
        <button className="btn btn-secondary btn-sm" onClick={() => onView(plan.id)}>
          View Details
        </button>
        {plan.plan_status === 'active' && onCreateInvoice && (
          <button className="btn btn-primary btn-sm" onClick={() => onCreateInvoice(plan.id)}>
            Create Invoice
          </button>
        )}
        {plan.plan_status === 'active' && onCancel && (
          <button className="btn btn-danger btn-sm" onClick={() => onCancel(plan.id)}>
            Cancel Plan
          </button>
        )}
      </div>
    </div>
  );
};

export default PaymentPlanCard;


